import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Table, Button, Badge, Spinner, Input } from "reactstrap";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";
import { HandleDateTimeAgo } from "../../../../Components/Common/HandleDateTimeAgo";

// Super Admin > Devices.
// Every TraceTail-fingerprinted device across ALL users, including the confidence
// score (which is hidden on the per-user My Devices tab). Developer-gated server-side
// via /userdevice/listall.
const SuperAdminDevices = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const loadDevices = async () => {
    setLoading(true);
    try {
      const res = await FetchAPI("/userdevice/listall", "GET");
      setDevices(res && res.userdevice_list ? res.userdevice_list : []);
    } catch (_) {
      setDevices([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const fmtDate = (value) => {
    if (!value) return "-";
    const d = new Date(value.replace(" ", "T"));
    return isNaN(d.getTime()) ? value : d.toLocaleString();
  };

  const fmtConfidence = (value) => {
    if (value == null || value === "") return "-";
    const n = Number(value);
    return isNaN(n) ? "-" : `${(n * 100).toFixed(1)}%`;
  };

  const fmtAgo = (value) =>
    value ? HandleDateTimeAgo(String(value).replace(" ", "T")) : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) =>
      [
        d.User_Email,
        d.User_UserFullName,
        d.UserDeviceFingerprint_DeviceName,
        d.UserDeviceFingerprint_LastSeenIpAddress,
        d.UserDeviceFingerprint_TracetailVisitorId,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [devices, query]);

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h5 className="mb-1">All Devices</h5>
            <p className="text-muted mb-0">
              Every recognised device across all users, with TraceTail confidence.
            </p>
          </div>
          <Button color="light" size="sm" onClick={loadDevices} disabled={loading}>
            <i className="ri-refresh-line align-middle me-1" />
            Refresh
          </Button>
        </div>

        <div className="mb-3" style={{ maxWidth: 360 }}>
          <Input
            type="search"
            placeholder="Search user, device, IP…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted py-5">No devices found.</div>
        ) : (
          <div className="table-responsive">
            <Table className="align-middle table-nowrap mb-0">
              <thead className="table-light">
                <tr>
                  <th>User</th>
                  <th>Device</th>
                  <th>Last login</th>
                  <th>Last active</th>
                  <th>Last IP</th>
                  <th>Confidence</th>
                  <th>Status</th>
                  <th>Visitor ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.UserDeviceFingerprint_Id}>
                    <td>
                      <div>
                        {d.User_UserFullName || (
                          <span className="text-muted fst-italic">Unknown</span>
                        )}
                      </div>
                      <div className="text-muted small">{d.User_Email || ""}</div>
                    </td>
                    <td>
                      <i className="ri-computer-line align-middle me-2 text-muted" />
                      {d.UserDeviceFingerprint_DeviceName || (
                        <span className="text-muted fst-italic">Unnamed</span>
                      )}
                    </td>
                    <td>{fmtDate(d.UserDeviceFingerprint_LastLoginDateTime)}</td>
                    <td>
                      <div>{fmtDate(d.UserDeviceFingerprint_LastActiveDateTime)}</div>
                      {fmtAgo(d.UserDeviceFingerprint_LastActiveDateTime) && (
                        <Badge color="light" className="text-muted mt-1">
                          {fmtAgo(d.UserDeviceFingerprint_LastActiveDateTime)}
                        </Badge>
                      )}
                    </td>
                    <td>{d.UserDeviceFingerprint_LastSeenIpAddress || "-"}</td>
                    <td>{fmtConfidence(d.UserDeviceFingerprint_Confidence)}</td>
                    <td>
                      {Number(d.UserDeviceFingerprint_TrustedFlag) === 1 ? (
                        <Badge color="success">Trusted</Badge>
                      ) : (
                        <Badge color="warning">Unconfirmed</Badge>
                      )}
                    </td>
                    <td className="text-muted small">
                      <code>{d.UserDeviceFingerprint_TracetailVisitorId || "-"}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default SuperAdminDevices;
